import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { FLAVOURS } from "../data/flavours";
import { useAuth } from "./AuthContext";
import { identify, trackAddedToCart } from "../utils/klaviyo";

const CartContext = createContext(null);

const FREE_SHIPPING_THRESHOLD = 25.00;
const STANDARD_SHIPPING_FEE = 4.00;

// Guests who gave their email in the popup or at checkout can still be identified
const readStoredEmail = () => {
  try {
    return localStorage.getItem("km_user_email") || localStorage.getItem("km_lead_email") || "";
  } catch {
    return "";
  }
};

export function CartProvider({ children }) {
  const { user } = useAuth();
  // Initialize cart from localStorage for session persistence
  const [items, setItems] = useState(() => {
    if (typeof window === "undefined") return [];
    try {
      const saved = localStorage.getItem("km_cart_items");
      return saved ? JSON.parse(saved) : [];
    } catch (err) {
      return [];
    }
  });

  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem("km_cart_items", JSON.stringify(items));
    } catch (err) {
      console.error("Failed to save cart:", err);
    }
  }, [items]);

  const openCart = useCallback(() => setOpen(true), []);
  const closeCart = useCallback(() => setOpen(false), []);
  const toggleCart = useCallback(() => setOpen((o) => !o), []);

  const addItem = useCallback((id, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((p) => p.id === id);
      if (existing) {
        return prev.map((p) =>
          p.id === id ? { ...p, qty: p.qty + qty } : p
        );
      }
      return [...prev, { id, qty }];
    });
    setOpen(true);
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const updateQty = useCallback((id, qty) => {
    setItems((prev) =>
      prev
        .map((p) => (p.id === id ? { ...p, qty: Math.max(0, qty) } : p))
        .filter((p) => p.qty > 0)
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const totalCount = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  const {
    detailed,
    subtotal,
    shippingFee,
    grandTotal,
    isFreeDelivery,
    amountNeededForFreeShipping,
    activeTierLabel,
    totalSavings,
  } = useMemo(() => {
    let unit = 2.50;
    let tierLabel = "Standard Price";

    if (totalCount >= 30) {
      unit = 1.40;
      tierLabel = "30+ Pack Rate (£1.40/ea)";
    } else if (totalCount >= 12) {
      unit = 19.0 / 12; // Evaluates to ~£1.5833, making exactly 12 pouches equal £19.00
      tierLabel = "The Krunch 12+ (£1.58/ea)";
    } else if (totalCount >= 6) {
      unit = 10.0 / 6; // Evaluates to ~£1.666, making exactly 6 equal £10.00
      tierLabel = "The Krunch 6 (£10 total)";
    } else if (totalCount >= 2) {
      unit = 2.00;
      tierLabel = "Duo Deal (£4 total)";
    }

    const calculatedItems = items.map((it) => {
      const flavour = FLAVOURS[it.id];
      const lineTotal = unit * it.qty;
      return {
        ...it,
        flavour,
        wooId: flavour?.wooId,
        unit,
        unitPrice: unit,
        lineTotal,
      };
    });

    const currentSubtotal = calculatedItems.reduce((s, i) => s + i.lineTotal, 0);
    const standardTotal = totalCount * 2.50;
    const savings = Math.max(0, standardTotal - currentSubtotal);

    const freeDelivery = currentSubtotal >= FREE_SHIPPING_THRESHOLD;
    const shipping = totalCount === 0 ? 0 : freeDelivery ? 0 : STANDARD_SHIPPING_FEE;
    const finalTotal = currentSubtotal + shipping;
    const remainingForFree = Math.max(0, FREE_SHIPPING_THRESHOLD - currentSubtotal);

    return {
      detailed: calculatedItems,
      subtotal: currentSubtotal,
      shippingFee: shipping,
      grandTotal: finalTotal,
      isFreeDelivery: freeDelivery,
      amountNeededForFreeShipping: remainingForFree,
      activeTierLabel: tierLabel,
      totalSavings: savings,
    };
  }, [items, totalCount]);

  // Klaviyo: identify the shopper and report lines whose quantity went up. The cart restored from
  // localStorage on page load is the starting point, so reloading a page sends nothing.
  const previousQty = useRef(null);
  useEffect(() => {
    const before = previousQty.current;
    previousQty.current = new Map(items.map((it) => [it.id, it.qty]));
    if (!before) return;

    const added = detailed
      .map((line) => ({ ...line, addedQty: line.qty - (before.get(line.id) || 0) }))
      .filter((line) => line.addedQty > 0);
    if (added.length === 0 && before.size === items.length) return;

    identify(user?.email || readStoredEmail());
    for (const line of added) trackAddedToCart(line, detailed, subtotal);
  }, [items, detailed, subtotal, user]);

  // Fill level for the empty pouch -> filled widget (25 pouches = 100%)
  const fillLevel = Math.min(100, Math.round((totalCount / 25) * 100));

  const checkout = useCallback(() => {
    if (detailed.length === 0) return;
    setOpen(false);
    window.location.href = "/checkout";
  }, [detailed]);

  const value = {
    items,
    detailed,
    totalCount,
    subtotal,
    shippingFee,
    grandTotal,
    freeShippingThreshold: FREE_SHIPPING_THRESHOLD,
    isFreeDelivery,
    amountNeededForFreeShipping,
    fillLevel,
    activeTierLabel,
    totalSavings,
    open,
    openCart,
    closeCart,
    toggleCart,
    addItem,
    removeItem,
    updateQty,
    clear,
    checkout,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
